import React, { useRef, useState, useEffect } from 'react';

const OTP_LENGTH = 6;

/**
 * Six single-digit auto-advancing OTP inputs.
 * - Auto-focus next box on entry, previous on backspace.
 * - Paste support: a full code pasted into any box fills all boxes.
 * - Fires onComplete once all digits are present.
 */
const OTPInput = ({ value, onChange, onComplete, disabled = false, autoFocus = true, hasError = false }) => {
  const digits = Array.isArray(value) ? value : String(value || '').split('');
  const boxes = Array.from({ length: OTP_LENGTH }, (_, i) => digits[i] || '');
  const inputRefs = useRef([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const focusBox = (index) => {
    const clamped = Math.max(0, Math.min(OTP_LENGTH - 1, index));
    inputRefs.current[clamped]?.focus();
    inputRefs.current[clamped]?.select();
  };

  const emit = (nextBoxes) => {
    const next = nextBoxes.map((b) => b || '');
    onChange?.(next);
    if (next.every((b) => b !== '') && onComplete) {
      onComplete(next.join(''));
    }
  };

  const handleChange = (index, raw) => {
    const clean = raw.replace(/\D/g, '');
    if (!clean) return;

    if (clean.length > 1) {
      // Paste of a multi-digit string into this box
      const next = [...boxes];
      for (let i = 0; i < clean.length && index + i < OTP_LENGTH; i++) {
        next[index + i] = clean[i];
      }
      emit(next);
      focusBox(index + clean.length);
      return;
    }

    const next = [...boxes];
    next[index] = clean;
    emit(next);
    if (index < OTP_LENGTH - 1) {
      focusBox(index + 1);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...boxes];
      if (next[index]) {
        next[index] = '';
        emit(next);
      } else if (index > 0) {
        next[index - 1] = '';
        emit(next);
        focusBox(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusBox(index - 1);
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      e.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '');
    if (!pasted) return;
    const next = Array.from({ length: OTP_LENGTH }, (_, i) => pasted[i] || '');
    emit(next);
    focusBox(Math.min(pasted.length, OTP_LENGTH - 1));
  };

  return (
    <div className="flex items-center justify-between gap-2 sm:gap-3" onPaste={handlePaste}>
      {boxes.map((digit, i) => (
        <input
          key={i}
          ref={(el) => (inputRefs.current[i] = el)}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={OTP_LENGTH}
          disabled={disabled}
          value={digit}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          className={`w-full aspect-square max-w-[52px] text-center text-xl font-black rounded-xl border-2 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none transition-all disabled:opacity-50 ${
            hasError
              ? 'border-red-400 dark:border-red-600 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
              : digit
                ? 'border-blue-500 dark:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                : 'border-slate-200 dark:border-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
          }`}
        />
      ))}
    </div>
  );
};

export default OTPInput;
